import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { FlexBox, Grid, MuiBox, Typography } from '@kis-technologies/kis-ui';
import TextTicket from 'assets/TextTicket.png';
import { ActionOverlay } from 'components/ActionOverlay';
import { selectors as cartSelectors } from 'services/cart';
import { selectors as configSelectors, Configuration, TicketDeliverySettings, Messages } from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { useStyles } from '../styled';
import { ComponentProps } from '../types';

export const TextDelivery = (props: ComponentProps) => {
  const { onCompletion, onErrorConfirmation } = props;
  const { t, i18n } = useTranslation();

  // Action Creators and Selectors
  const checkoutCart = useSelector(cartSelectors.selectCheckoutCart);
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const deliveryStatus = useSelector(ticketingSelectors.selectDeliveryStatus);
  const ticketingError = useSelector(ticketingSelectors.selectError);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const support = get(configuration, `${ticketingPath}.supportName.${i18n.language}`, t('commonText.support'));
  const textDeliveryConfig: TicketDeliverySettings['textMessage'] = get(
    configuration,
    `${ticketingPath}.ticketDeliverySettings.textMessage`,
    {}
  );
  const deliveryMessages: Messages = get(textDeliveryConfig, `deliveryInProgressMessages`, {});
  const deliveryHeaderText: string = get(
    deliveryMessages,
    `header.${i18n.language}`,
    `${t('delivery.sendingYour')} ${t('delivery.tickets')}`
  );
  const deliverySubheaderText: string = get(
    deliveryMessages,
    `subheader.${i18n.language}`,
    t('delivery.checkPhoneForTicket')
  );
  const errorOverlayMessages: Messages = get(textDeliveryConfig, `errorOverlayMessages`, {});
  const errorOverlayHeaderText: string = get(
    errorOverlayMessages,
    `header.${i18n.language}`,
    t('delivery.deliveryError')
  );
  const errorOverlaySubheaderText: string = get(errorOverlayMessages, `subheader.${i18n.language}`, '');
  const errorOverlayConfirmText: string = get(errorOverlayMessages, `buttons.${i18n.language}[0]`, t('commonText.ok'));

  const classes = useStyles();

  useEffect(() => {
    // On completion
    if (deliveryStatus === 'completed') {
      onCompletion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryStatus]);

  /**
   * Returns text to use as the subheader for the error overlay.
   * The configuration allows for one macro: {{orderId}}. If not configured,
   * there are two default strings depending on if the orderId is known.
   */
  const getErrorOverlaySubheader = (): string => {
    let errorMessage: string;
    const orderId = (checkoutCart && checkoutCart.orderId) || '';

    if (errorOverlaySubheaderText) {
      errorMessage = errorOverlaySubheaderText.replace('{{orderId}}', orderId);
    } else if (orderId) {
      errorMessage = t('saleError.printErrorMsg', { orderId: `#${orderId}`, support });
    } else {
      errorMessage = t('saleError.notifySupport', { support });
    }

    return errorMessage;
  };

  return (
    <>
      <Grid className={classes.deliveryOuterContainer}>
        <Grid container className={classes.deliveryInnerContainer}>
          <Grid item lg={12} xl={6} className={classes.deliveryTextContainer}>
            {/* Until text api call, show text content - otherwise, re-route to CompletionView */}
            <MuiBox className={classes.deliveryPrintingText}>
              <Grid className={classes.deliveryPrintingHeader}>
                <Typography
                  fontSize={100}
                  fontWeight={600}
                  sx={{ lineHeight: 1.1, whiteSpace: 'pre-wrap' }}
                  align="center"
                >
                  {deliveryHeaderText}
                </Typography>
              </Grid>
              <Typography fontSize={30}>{deliverySubheaderText}</Typography>
            </MuiBox>
          </Grid>
          <Grid item lg={12} xl={6} className={classes.deliveryImageContainer}>
            <FlexBox fullHeight padding="0 20" align="center" justify="center">
              <img src={TextTicket} alt="text-ticket" width="100%" />
            </FlexBox>
          </Grid>
        </Grid>
      </Grid>

      {ticketingError && (
        <ActionOverlay
          title={errorOverlayHeaderText}
          description={
            <Typography align="center" fontSize={30}>
              {getErrorOverlaySubheader()}
            </Typography>
          }
          confirmText={errorOverlayConfirmText}
          onConfirm={onErrorConfirmation}
          height={500}
          style={{ background: 'rgba(255,255,255,0.95)' }}
        />
      )}
    </>
  );
};
